import React from 'react';
import { usePageEditMode } from '@/context/PageEditModeContext';
import { Plus, Trash2, ArrowUp, ArrowDown, Copy } from 'lucide-react';

/**
 * Editable array wrapper. Reads the current list at `path` (falling back to
 * `fallback` when nothing saved yet), renders each row via `renderItem`, and
 * when in edit mode overlays add / delete / reorder / duplicate controls.
 *
 * Props:
 *   path        dotted config path where the array lives
 *   fallback    initial array shown when nothing is saved
 *   emptyItem   () => object used when admin clicks "+ Add row"
 *   renderItem  (item, index) => ReactNode
 *   className   optional wrapper class
 *   itemWrapper optional class applied to each row's outer <div>
 */
export const EditableList = ({
  path,
  fallback = [],
  emptyItem = () => ({}),
  renderItem,
  className = '',
  itemWrapper = '',
}) => {
  const { enabled, getValue, update } = usePageEditMode();
  const saved = getValue(path);

  // Migrate legacy "object with numeric keys" (produced by per-index PageEditable
  // writes before EditableList existed) into a proper array. For each saved
  // index, FIELD-MERGE on top of the fallback item so untouched fields (image,
  // bio, role, …) survive when the admin only edits one field like `name`.
  const list = React.useMemo(() => {
    if (Array.isArray(saved)) return saved;
    if (saved && typeof saved === 'object') {
      const base = Array.isArray(fallback) ? [...fallback] : [];
      const numericKeys = Object.keys(saved).filter(k => /^\d+$/.test(k));
      if (!numericKeys.length) return base;
      const maxIdx = Math.max(...numericKeys.map(Number), base.length - 1);
      const merged = [];
      for (let i = 0; i <= maxIdx; i++) {
        const baseItem = (i < base.length) ? base[i] : null;
        if (Object.prototype.hasOwnProperty.call(saved, String(i))) {
          const override = saved[String(i)];
          // Field-level merge: keep baseItem's image/bio/etc when override
          // only sets a subset of fields.
          merged.push(
            baseItem && typeof baseItem === 'object' && override && typeof override === 'object'
              ? { ...baseItem, ...override }
              : override
          );
        } else if (baseItem) {
          merged.push(baseItem);
        }
      }
      return merged;
    }
    return Array.isArray(fallback) ? fallback : [];
  }, [saved, fallback]);

  const write = (next) => update(path, next);

  const addRow = () => write([...list, emptyItem()]);
  const removeRow = (i) => write(list.filter((_, idx) => idx !== i));
  const duplicateRow = (i) => {
    const copy = JSON.parse(JSON.stringify(list[i] || {}));
    const next = [...list];
    next.splice(i + 1, 0, copy);
    write(next);
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    write(next);
  };

  return (
    <div className={className}>
      {list.map((item, i) => (
        <div key={i} className={`relative group ${itemWrapper}`}>
          {enabled && (
            <div
              data-edit-safe
              className="absolute -top-3 -right-3 z-20 flex gap-1 bg-black/85 border border-yellow-400/70 rounded-full px-1 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <button onClick={() => move(i, -1)} title="Move up" className="p-1 text-white hover:text-yellow-300">
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => move(i, 1)} title="Move down" className="p-1 text-white hover:text-yellow-300">
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => duplicateRow(i)} title="Duplicate" className="p-1 text-white hover:text-yellow-300">
                <Copy className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => removeRow(i)} title="Delete" className="p-1 text-red-400 hover:text-red-300">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {renderItem(item, i)}
        </div>
      ))}
      {enabled && (
        <button
          type="button"
          data-edit-safe
          onClick={addRow}
          className="mt-4 mx-auto flex items-center gap-2 px-4 py-2 bg-yellow-400/20 hover:bg-yellow-400/30 border-2 border-dashed border-yellow-400/70 rounded-xl text-yellow-100 font-semibold"
        >
          <Plus className="w-4 h-4" /> Add row
        </button>
      )}
    </div>
  );
};

export default EditableList;
